import { authUserData } from ".";
import { User } from "../my-type";
import {
  type UpdateUserLeadRoleRequest,
  UserService as GeneratedUserService,
  callApi,
  configureApiClient,
} from "../api/client";

export interface UserSearchParams {
  id?: number;
  email?: string;
  displayName?: string;
  userName?: string;
  teamCode?: string;
}

const configureUserApiClient = (token: string) => {
  configureApiClient({
    tokenProvider: () => token,
  });
};

const toUserError = (error: unknown): Error => {
  const e = error as Error & { status?: number };
  if (e?.status === 401) {
    return new Error("Unauthorized access - invalid or expired token");
  }
  if (e?.status === 403) {
    return new Error("Insufficient permissions");
  }
  return e instanceof Error ? e : new Error("Failed to search users");
};

export const searchUsers = async (searchParams: UserSearchParams): Promise<User[]> => {
  const authUser = authUserData();
  const token = authUser?.jwtToken;

  if (!token) {
    throw new Error("No authentication token found");
  }

  try {
    configureUserApiClient(token);

    const data = await callApi(
      () =>
        GeneratedUserService.getApiUserSearch(
          searchParams.id !== undefined ? undefined : searchParams.email,
          searchParams.id !== undefined ? undefined : searchParams.displayName,
          searchParams.id !== undefined ? undefined : searchParams.userName,
          searchParams.id,
          searchParams.id !== undefined ? undefined : searchParams.teamCode
        ),
      "Failed to search users"
    );

    return data as unknown as User[];
  } catch (error) {
    throw toUserError(error);
  }
};

export const searchUserById = async (id: number): Promise<User | null> => {
  const users = await searchUsers({ id });
  return users.length > 0 ? users[0] : null;
};

export const getUserById = async (id: number): Promise<User | null> => {
  const authUser = authUserData();
  const token = authUser?.jwtToken;

  if (!token) {
    throw new Error("No authentication token found");
  }

  try {
    configureUserApiClient(token);

    const data = await callApi(
      () => GeneratedUserService.getApiUser(id),
      "Failed to get user"
    );

    return data as unknown as User;
  } catch (error) {
    throw toUserError(error);
  }
};

export const searchUsersMultiple = async (criteria: {
  email?: string;
  displayName?: string;
  userName?: string;
  teamCode?: string;
}): Promise<User[]> => {
  return searchUsers(criteria);
};

export const updateUserLeadRole = async (
  request: UpdateUserLeadRoleRequest
): Promise<User> => {
  const authUser = authUserData();
  const token = authUser?.jwtToken;

  if (!token) {
    throw new Error("No authentication token found");
  }

  try {
    configureUserApiClient(token);

    const data = await callApi(
      () => GeneratedUserService.postApiUserLeadRole(request),
      "Failed to update lead role"
    );

    return data as unknown as User;
  } catch (error) {
    throw toUserError(error);
  }
};

const UserService = {
  searchUsers,
  searchUserById,
  getUserById,
  searchUsersMultiple,
  updateUserLeadRole,
};

export default UserService;
